import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Controller } from 'react-hook-form';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { Button, Input, Dialog, Tooltip } from '@dsi/react-eds';
import styles from '../swgw-scanning/EditProductNumberDialog.module.css';
import { useImagePathTagForm } from './use-image-path-tag-form';
import '../swgw-scanning/Settings.css';
import { useLoader } from '../../../hooks/useLoader';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import api from '../../../api/axiosInstance';
import { AxiosError } from 'axios';

export interface EditImagePathTagDialogProps {
  repositoryId: string;
  open: boolean;
  initialItem: { image_path: string; tag: string } | null;
  onSave: () => void;
  onClose: () => void;
}

const EditImagePathTagDialog: React.FC<EditImagePathTagDialogProps> = ({ repositoryId, open, initialItem, onSave, onClose }) => {
  const methods = useImagePathTagForm(initialItem || undefined);
  const { handleSubmit, formState: { errors, isDirty, isValid }, reset, control } = methods;
  const { addNotifications } = useSwobNotifications();
  const { showLoader, hideLoader } = useLoader();

  useEffect(() => {
    if (open && initialItem) {
      reset(initialItem);
    }

    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open, initialItem, reset]);

  const editImagePathTag = async (body: { image_path: string, tag: string }) => {
    showLoader();
    try {
      const resp = await api.put(`/api/jfrog-config/${repositoryId}/image-path-tags`, {
        imagePathTags: [body]
      });

      if (resp.data.failed && resp.data.failed.length > 0) {
        addNotifications([
          {
            title: 'Error - Image Path & Tag',
            icon: 'icon-triangle-warning',
            description: `${resp.data?.failed[0]?.error}` || `Failed to update Image Path ${body.image_path}`,
            timestamp: new Date(),
            color: 'red'
          }
        ]);
      } else {
        addNotifications([
          {
            title: 'Success',
            icon: 'icon-success',
            description: `Image path ${body.image_path} with tag ${body.tag} was successfully updated!`,
            timestamp: new Date(),
            color: 'green',
          },
        ]);
        onSave();
      }
    } catch (error: unknown) {
      let errorMsg = `An error occurred while updating the image path ${body.image_path}.`;
      if (error instanceof AxiosError) {
        errorMsg = error?.response?.data?.message || error?.message;
      }
      addNotifications([
        {
          title: 'Error - Image Path & Tag',
          icon: 'icon-triangle-warning',
          description: errorMsg,
          timestamp: new Date(),
          color: 'red',
        },
      ]);
      onClose();
    } finally {
      setTimeout(() => {
        hideLoader();
      }, 500);
    }
  };

  return ReactDOM.createPortal(
    <div id="edit-image-path-tag-dialog">
      <ReactEDSWrapper
        component={Dialog}
        show={open}
        onClose={onClose}
        title="Edit Image Path & Tag"
        subtitle="JFrog Artifactory Configuration"
        bottom={
          <ReactEDSWrapper
            component={Button}
            variant="primary"
            className="btn primary"
            type="submit"
            form="edit-image-path-tag-form"
            disabled={!isDirty || !isValid}
          >
            Save
          </ReactEDSWrapper>
        }
      >
        <form
          id="edit-image-path-tag-form"
          onSubmit={handleSubmit((data) => {
            editImagePathTag(data);
          })}
          className={styles.dialogForm}
        >
          <label htmlFor="edit-image-path" className={styles.label}>
            Image Path <span className={styles.requiredAsterisk}> *</span>
            <ReactEDSWrapper
              component={Tooltip}
              message="The path to the container image in JFrog Artifactory"
              pointer
            >
              <span>
                <i className={`icon icon-info ${styles.infoIcon}`}></i>
              </span>
            </ReactEDSWrapper>
          </label>
          <Controller
            name="image_path"
            control={control}
            render={({ field }) => (
              <ReactEDSWrapper
                component={Input}
                id="edit-image-path"
                type="text"
                placeholder="e.g., my-project/my-image"
                {...field}
              />
            )}
          />
          <div>
            {errors.image_path ? (
              <span className={styles.error}>
                <span className={styles.dangerIcon} aria-label="error" role="img">&#9888;</span>
                {typeof errors.image_path.message === 'string' ? errors.image_path.message : 'Required'}
              </span>
            ) : null}
          </div>
          <label htmlFor="edit-tag" className={styles.label}>
            Tag <span className={styles.requiredAsterisk}> *</span>
            <ReactEDSWrapper
              component={Tooltip}
              message="The image tag (version) in JFrog Artifactory"
              pointer
            >
              <span>
                <i className={`icon icon-info ${styles.infoIcon}`}></i>
              </span>
            </ReactEDSWrapper>
          </label>
          <Controller
            name="tag"
            control={control}
            render={({ field }) => (
              <ReactEDSWrapper
                component={Input}
                id="edit-tag"
                type="text"
                placeholder="e.g., latest, v1.0.0"
                {...field}
              />
            )}
          />
          <div>
            {errors.tag ? (
              <span className={styles.error}>
                <span className={styles.dangerIcon} aria-label="error" role="img">&#9888;</span>
                {typeof errors.tag.message === 'string' ? errors.tag.message : 'Required'}
              </span>
            ) : null}
          </div>
        </form>
      </ReactEDSWrapper>
    </div>, document.body
  );
};

export default EditImagePathTagDialog;
